import type { ReactNode } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import type { CategoryDistribution, DistributionItem } from '@/utils/evidenceDistributionMetrics';

export type CardAccent = {
  bar: string;
  bg: string;
  text: string;
  badgeBg: string;
  badgeText: string;
};

export type EvidenceDistributionCardProps = {
  distribution: CategoryDistribution;
  accent: CardAccent;
  icon?: ReactNode;
  maxInitialItems?: number;
  compact?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  onItemClick?: (label: string) => void;
};

export function EvidenceDistributionCard({
  distribution,
  accent,
  icon,
  maxInitialItems = 5,
  compact = false,
  expanded = false,
  onExpandedChange,
  onItemClick,
}: EvidenceDistributionCardProps) {
  const {
    title,
    items,
    totalAnnotated,
    totalUnspecified,
    totalRecords,
    isComplete = true,
    sampledAnnotated = totalAnnotated,
    sampleWindowSize,
  } = distribution;

  const visibleItems = expanded ? items : items.slice(0, maxInitialItems);
  const hasHiddenItems = items.length > maxInitialItems;
  const badgeTitle = isComplete
    ? `${totalAnnotated} of ${totalRecords} records annotated`
    : `${sampledAnnotated} of ${totalAnnotated} annotated shown (${totalUnspecified} unspecified of ${totalRecords})`;
  const badgeText = isComplete
    ? `${totalAnnotated} / ${totalRecords} records`
    : `${sampledAnnotated}/${totalAnnotated} annotated`;

  const incompleteHint = (() => {
    if (isComplete || sampledAnnotated <= 0) return null;
    const windowSize = sampleWindowSize ?? sampledAnnotated;
    if (windowSize > sampledAnnotated) {
      return `Found ${sampledAnnotated.toLocaleString()} annotated values in a sample of ${windowSize.toLocaleString()} evidence records (≈${totalAnnotated.toLocaleString()} annotated of ${totalRecords.toLocaleString()} total). Distribution may be incomplete.`;
    }

    return `Distribution from first ${sampledAnnotated.toLocaleString()} of ${totalAnnotated.toLocaleString()} annotated records. Values may be incomplete.`;
  })();

  const padX = compact ? 'px-2.5' : 'px-3.5';
  const hasFooter = (hasHiddenItems && onExpandedChange) || totalUnspecified > 0 || Boolean(incompleteHint);

  return (
    <div className="flex h-full min-h-0 flex-col rounded-xl border-x border-b border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      <div data-testid="evidence-distribution-card-header" className="sticky top-0 z-10 shrink-0">
        <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-full h-3 bg-white" />
        <div
          className={`relative z-[1] flex items-center justify-between gap-2 rounded-t-xl border-t border-b border-slate-100 bg-white ${padX} ${
            compact ? 'pb-2 pt-2.5' : 'pb-2.5 pt-3.5'
          }`}
        >
          <div className="flex min-w-0 items-center gap-2">
            {icon && (
              <span
                className={`flex shrink-0 items-center justify-center rounded-lg ${
                  compact ? 'h-6 w-6' : 'h-7 w-7'
                }`}
                style={{ backgroundColor: accent.bg, color: accent.text }}
              >
                {icon}
              </span>
            )}
            <div className="min-w-0">
              <h4 className="truncate text-sm font-semibold uppercase tracking-wider text-slate-700">
                {title}
              </h4>
            </div>
          </div>
          <span
            className="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
            style={{ backgroundColor: accent.badgeBg, color: accent.badgeText }}
            title={badgeTitle}
          >
            {badgeText}
          </span>
        </div>
      </div>

      {/* Content */}
      <div
        className={`min-h-0 flex-1 overflow-y-auto ${padX} ${
          compact ? 'space-y-1.5 pt-2' : 'space-y-2.5 pt-3'
        } ${hasFooter ? '' : compact ? 'pb-2.5' : 'pb-3.5'}`}
      >
        {items.length === 0 ? (
          <div
            className={`flex items-center justify-center text-center text-sm text-slate-400 ${
              compact ? 'min-h-[2.5rem] py-1' : 'h-24'
            }`}
          >
            No {title.toLowerCase()} data in evidence records
          </div>
        ) : (
          visibleItems.map(item => (
            <DistributionRow
              key={item.label}
              item={item}
              accent={accent}
              compact={compact}
              onClick={onItemClick ? () => onItemClick(item.label) : undefined}
            />
          ))
        )}
      </div>

      {hasFooter && (
        <div className={`shrink-0 ${padX} ${compact ? 'pb-2.5' : 'pb-3.5'}`}>
          {hasHiddenItems && onExpandedChange && (
            <button
              type="button"
              onClick={() => onExpandedChange(!expanded)}
              className={`flex w-full items-center justify-center gap-1 border-t border-slate-100 text-sm font-medium text-slate-500 transition-colors hover:text-slate-800 ${
                compact ? 'pt-1.5' : 'pt-2'
              }`}
            >
              {expanded ? (
                <>
                  Show less <ChevronUpIcon className="h-3.5 w-3.5" />
                </>
              ) : (
                <>
                  Show {items.length - maxInitialItems} more <ChevronDownIcon className="h-3.5 w-3.5" />
                </>
              )}
            </button>
          )}

          {totalUnspecified > 0 && (
            <div
              className={`flex items-center justify-between border-t border-slate-100 text-xs text-slate-400 ${
                compact ? 'pt-1.5' : 'pt-2'
              }`}
            >
              <span>Unspecified in document</span>
              <span className="font-semibold text-slate-500">
                {totalUnspecified.toLocaleString()}
                {totalRecords > 0 ? ` (${Math.round((totalUnspecified / totalRecords) * 100)}%)` : ''}
              </span>
            </div>
          )}
          {!isComplete && incompleteHint && (
            <p className="mb-0 mt-2 text-xs leading-snug text-amber-700">{incompleteHint}</p>
          )}
        </div>
      )}
    </div>
  );
}

function DistributionRow({
  item,
  accent,
  compact = false,
  onClick,
}: {
  item: DistributionItem;
  accent: CardAccent;
  compact?: boolean;
  onClick?: () => void;
}) {
  const content = (
    <div className={`group flex flex-col ${compact ? 'gap-0.5' : 'gap-1'}`}>
      <div className="flex items-center justify-between text-sm">
        <span
          className="truncate font-medium text-slate-700 transition-colors group-hover:text-slate-900"
          title={item.label}
        >
          {item.label}
        </span>
        <span className="ml-2 shrink-0 font-semibold text-slate-600">
          {item.count} <span className="font-normal text-slate-400">({item.percentage}%)</span>
        </span>
      </div>
      <div className={`w-full overflow-hidden rounded-full bg-slate-100 ${compact ? 'h-1' : 'h-1.5'}`}>
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{
            width: `${Math.max(item.percentage, 2)}%`,
            backgroundColor: accent.bar,
          }}
        />
      </div>
    </div>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left transition-opacity hover:opacity-85 focus:outline-none"
      >
        {content}
      </button>
    );
  }

  return <div>{content}</div>;
}
